import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  Button, 
  message, 
  Popconfirm,
  Tag,
  Space,
  Divider
} from 'antd';
import { 
  DeleteOutlined, 
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  GiftOutlined,
  DollarOutlined,
  CloseOutlined,
  EditOutlined
} from '@ant-design/icons';
import { giftService, GiftGiver } from '../../services/giftService';
import styles from '../common/DrawerModalMobile.module.css';

interface GiftGiversDetailModalMobileProps {
  isOpen: boolean;
  onClose: () => void;
  giver?: GiftGiver | null;
  onSuccess: () => void;
  onDelete?: () => void;
  onEdit?: (giver: GiftGiver) => void;
}

export const GiftGiversDetailModalMobile: React.FC<GiftGiversDetailModalMobileProps> = ({
  isOpen,
  onClose,
  giver,
  onSuccess,
  onDelete,
  onEdit
}) => {
  const [loading, setLoading] = useState(false);
  const [giftStats, setGiftStats] = useState({
    totalAmount: 0,
    giftCount: 0
  });

  useEffect(() => {
    if (isOpen && giver) {
      // Load gift statistics
      loadGiftStats();
    }
  }, [isOpen, giver]);

  const loadGiftStats = async () => {
    if (!giver) return;
    
    try {
      const giftsResponse = await giftService.getGifts({ giver_id: giver.id });
      const totalAmount = giftsResponse.data.reduce((sum: number, gift: any) => sum + (gift.amount || 0), 0);
      const giftCount = giftsResponse.data.length;
      
      setGiftStats({
        totalAmount,
        giftCount
      });
    } catch (error) {
      console.error('Error loading gift stats:', error);
    }
  };

  const getRelationshipLabel = (relationship: string) => {
    const labels: Record<string, string> = {
      family_internal: 'Gia đình nội',
      family_external: 'Gia đình ngoại',
      friend_bride: 'Bạn cô dâu',
      friend_groom: 'Bạn chú rể',
      colleague: 'Đồng nghiệp',
      business_partner: 'Đối tác',
      other: 'Khác'
    };
    return labels[relationship] || relationship;
  };

  const getRelationshipColor = (relationship: string) => {
    const colors: Record<string, string> = {
      family_internal: 'red',
      family_external: 'orange',
      friend_bride: 'pink',
      friend_groom: 'blue',
      colleague: 'green',
      business_partner: 'purple',
      other: 'default'
    };
    return colors[relationship] || 'default';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const handleDelete = async () => {
    if (!giver) return;
    
    try {
      setLoading(true);
      await giftService.deleteGiftGiver(giver.id);
      message.success('Xóa khách mời thành công');
      onDelete?.();
      onClose();
    } catch (error) {
      message.error('Không thể xóa khách mời');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    if (giver && onEdit) {
      onEdit(giver);
    }
  };

  return (
    <Drawer
      title="Chi tiết khách mời"
      open={isOpen}
      onClose={onClose}
      placement="bottom"
      height="90%"
      className={styles.drawer}
      bodyStyle={{ padding: 0 }}
      headerStyle={{ 
        borderBottom: '1px solid #f0f0f0',
        padding: '16px 20px'
      }}
    >
      <div className={styles.drawerContent}>
        <div className={styles.drawerBody}>
          {giver && (
            <>
              {/* Basic Information */}
              <div className={styles.drawerTable}>
                <div className={styles.drawerTableRow}>
                  <div className={styles.drawerTableLabel}>Họ và tên:</div>
                  <div className={styles.drawerTableValue}>
                    <strong>{giver.full_name}</strong>
                  </div>
                </div>
                
                <div className={styles.drawerTableRow}>
                  <div className={styles.drawerTableLabel}>Mối quan hệ:</div>
                  <div className={styles.drawerTableValue}>
                    <Tag color={getRelationshipColor(giver.relationship)}>
                      {getRelationshipLabel(giver.relationship)}
                    </Tag>
                  </div>
                </div>
                
                {giver.phone && (
                  <div className={styles.drawerTableRow}>
                    <div className={styles.drawerTableLabel}>Số điện thoại:</div>
                    <div className={styles.drawerTableValue}>
                      <Space>
                        <PhoneOutlined />
                        {giver.phone}
                      </Space>
                    </div>
                  </div>
                )}
                
                {giver.email && (
                  <div className={styles.drawerTableRow}>
                    <div className={styles.drawerTableLabel}>Email:</div>
                    <div className={styles.drawerTableValue}>
                      <Space>
                        <MailOutlined />
                        {giver.email}
                      </Space>
                    </div>
                  </div>
                )}
                
                {giver.address && (
                  <div className={styles.drawerTableRow}>
                    <div className={styles.drawerTableLabel}>Địa chỉ:</div>
                    <div className={styles.drawerTableValue}>
                      <Space>
                        <HomeOutlined />
                        {giver.address}
                      </Space>
                    </div>
                  </div>
                )}
                
                {giver.social_media && (
                  <div className={styles.drawerTableRow}>
                    <div className={styles.drawerTableLabel}>Mạng xã hội:</div>
                    <div className={styles.drawerTableValue}>
                      {giver.social_media}
                    </div>
                  </div>
                )}
                
                {giver.notes && (
                  <div className={styles.drawerTableRow}>
                    <div className={styles.drawerTableLabel}>Ghi chú:</div>
                    <div className={styles.drawerTableValue}>
                      {giver.notes}
                    </div>
                  </div>
                )}
              </div>

              <Divider />

              {/* Gift Statistics */}
              <div style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  marginBottom: '12px',
                  fontWeight: 600,
                  color: '#333'
                }}>
                  <GiftOutlined style={{ color: '#FF89A1' }} />
                  Thống kê quà tặng
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      Tổng tiền mừng:
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#FF89A1' }}>
                      <DollarOutlined style={{ marginRight: '4px' }} />
                      {formatCurrency(giftStats.totalAmount)}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      Số món quà:
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#FF89A1' }}>
                      {giftStats.giftCount} món
                    </div>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className={styles.drawerTableRow}>
                <div className={styles.drawerTableLabel}>Trạng thái:</div>
                <div className={styles.drawerTableValue}>
                  <Tag color={giver.is_active ? 'green' : 'red'}>
                    {giver.is_active ? 'Hoạt động' : 'Không hoạt động'}
                  </Tag>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.drawerFooter}>
          <div className={styles.drawerFooterRow}>
            {onEdit && (
              <Button
                className={`${styles.drawerFooterButton} primary`}
                onClick={handleEditClick}
                icon={<EditOutlined />}
              >
                Chỉnh sửa
              </Button>
            )}
            {onDelete && (
              <Popconfirm
                title="Bạn có chắc muốn xóa khách mời này?"
                onConfirm={handleDelete}
                okText="Có"
                cancelText="Không"
              >
                <Button
                  danger
                  className={styles.drawerFooterButton}
                  icon={<DeleteOutlined />}
                  loading={loading}
                >
                  Xóa khách mời
                </Button>
              </Popconfirm>
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
};
